import { getDoc, doc } from "firebase/firestore";
import { db } from "firebaseConfig";
import { useEffect, useRef, useState } from "react";
import { mapConverter, MapType } from "types/firestoreTypes";
import sleep from "modules/sleep";
import distance from "modules/distance";
import map_4 from "./images/map_4.svg";
import map_5 from "./images/map_5.svg";
import map_6 from "./images/map_6.svg";

type Floor = 4 | 5 | 6;

const useFloorMap = ({
    initFloor,
    dest,
    destFloor,
}: {
    initFloor: Floor;
    dest: string | null;
    destFloor: number | null;
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const getContext = () => {
        if (!canvasRef.current) throw new Error("canvas is null");
        const res = canvasRef.current.getContext("2d");
        if (!res) throw new Error("context is null");
        return res;
    };

    navigator.geolocation.watchPosition(
        (pos) => {
            currentLocationLatitude.current = pos.coords.latitude;
            currentLocationLongitude.current = pos.coords.longitude;
            if (!pos.coords.altitude || isFloorChange.current) {
                return;
            }
            if (pos.coords.altitude < 54) {
                if (floor.current !== 4) floorChange(4);
            } else if (pos.coords.altitude < 57) {
                if (floor.current !== 5) floorChange(5);
            } else {
                if (floor.current !== 6) floorChange(6);
            }
        },
        (e) => {
            if (e.code === 1) {
                alert("エラー\n位置情報取得の権限がありません");
            } else {
                alert("エラー\n位置情報が取得できませんでした");
            }
        },
        {
            enableHighAccuracy: true,
        }
    );

    // const [floor, setFloor] = useState<Floor>(initFloor);
    const floor = useRef<Floor>(initFloor);
    const currentLocationLatitude = useRef(0);
    const currentLocationLongitude = useRef(0);
    const [destination, setDestination] = useState(dest);
    const [destinationFloor, setDestinationFloor] = useState(destFloor);
    const [destinationLocationLatitude, setDestinationLocationLatitude] =
        useState(0);
    const [destinationLocationLongitude, setDestinationLocationLongitude] =
        useState(0);
    const isFloorChange = useRef(false);
    const [shopList, setShopList] = useState<MapType | null>(null);

    const dijkstra = async () => {
        // 目的地が存在しない場合は終了
        if (!(destination && destinationFloor)) {
            return;
        }
        if (!shopList) throw new Error("shop list is null");
        const nowFloor = floor.current;
        let destinationIds = shopList.node.floor[floor.current]
            .map((val, i) => (val.name === destination ? i : undefined))
            .filter((v) => v !== undefined) as number[];

        const len = shopList.node.floor[floor.current].length;

        const f = () => {
            let min = 10e18;
            let id: number = -1;
            // 現在地に一番近いnodeを探索
            shopList.node.floor[floor.current].forEach((v, i) => {
                const tmp = distance(
                    currentLocationLatitude.current,
                    currentLocationLongitude.current,
                    v.latitude,
                    v.longitude
                );
                if (tmp < min) {
                    min = tmp;
                    id = i;
                }
            });
            /** 各nodeからnodeまでのコスト */
            const cost = shopList.edge.floor[4].map((v) => v.to);
            /** nodes[id]からの最短距離 */
            let nodes: number[] = new Array(len).fill(10e18);
            /** node[i]が探索済みか */
            let isSeen: boolean[] = new Array(len).fill(false);
            /** node[i]への最短経路の一つ手前 経路復元に使用する */
            let prev: number[] = new Array(len).fill(-1);
            nodes[id] = 0;

            // ダイクストラ法による探索
            // 参考:https://kuruton.hatenablog.com/entry/2020/12/02/080000
            while (true) {
                let v = -1;
                for (let i = 0; i < len; i++) {
                    if (!isSeen[i] && (v === -1 || nodes[i] < nodes[v])) {
                        v = i;
                    }
                }
                if (v === -1) {
                    break;
                }
                isSeen[v] = true;
                for (let i = 0; i < len; i++) {
                    if (nodes[i] > nodes[v] + cost[v][i]) {
                        nodes[i] = nodes[v] + cost[v][i];
                        prev[i] = v;
                    }
                }
            }

            return { nodes, prev };
        };

        // 目的地と現在地の階が同じでない場合
        if (floor.current !== destinationFloor) {
            destinationIds = shopList.node.floor[floor.current]
                .map((v, i) =>
                    v.is_elevator ||
                    v.is_stairs ||
                    (destinationFloor! > floor.current
                        ? v.is_up_escalator
                        : v.is_down_escalator)
                        ? i
                        : undefined
                )
                .filter((v) => v !== undefined) as number[];

            do {
                const { nodes, prev } = f();
                const min = Math.min(
                    ...nodes.filter((_, i) =>
                        destinationIds.some((a) => a === i)
                    )
                );

                const index = nodes
                    .map((v, i) =>
                        v === min && destinationIds.some((a) => a === i)
                            ? i
                            : undefined
                    )
                    .filter((v) => v !== undefined) as number[];

                index.forEach((v) => {
                    // 経路復元
                    let route: number[] = [];
                    let t = v;
                    while (t !== -1) {
                        route.push(t);
                        t = prev[t];
                    }
                    route.reverse();
                    show(route);
                });

                // 1秒停止
                await sleep(1000);
            } while (nowFloor === floor.current);
        } else {
            // 目的地と現在地の階が同じの場合
            // 階が同じかつ、現在地と目的地の距離が遠い間ループ
            while (
                nowFloor === floor.current &&
                distance(
                    currentLocationLatitude.current,
                    currentLocationLongitude.current,
                    destinationLocationLatitude,
                    destinationLocationLongitude
                ) > 3
            ) {
                const { prev } = f();
                // 経路復元
                let route: number[] = [];
                let t = destinationIds[0];
                while (t !== -1) {
                    route.push(t);
                    t = prev[t];
                }
                route.reverse();
                show(route);
                // 1秒停止
                await sleep(1000);
            }
        }
    };

    const show = (route: number[] = []) => {
        if (!shopList) throw new Error("shop list is null");
        if (!canvasRef.current) throw new Error("canvas ref is null");
        const a = shopList.node.floor[floor.current];
        // Map画像を長方形abcdとした際のあれのあれ
        /** 横のメートル */
        const ab = 15;
        /** 縦のメートル */
        // const ac = 25;
        /** canvasの横幅(px) */
        const w = canvasRef.current.width;
        /** canvasの縦幅(px) */
        const h = canvasRef.current.height;
        /** canvasのpxとメートルの倍率 */
        const mag = w / ab;
        const [ulLat, ulLng] = shopList.origin;
        const [urLat, urLng] = shopList.upper_right;
        const [llLat, llLng] = shopList.lower_left;
        const [lrLat, lrLng] = shopList.lower_right;
        // カリー化
        const d =
            (lat1: number, lng1: number) => (lat2: number, lng2: number) =>
                distance(lat1, lng1, lat2, lng2);

        /**
         * 長方形abcd内の任意の点Pについて、xy座標とabcdからの距離を引数にとり、Pとの誤差を返す関数
         * @param x x
         * @param y y
         * @param ar aからPまでの距離
         * @param br bからPまでの距離
         * @param cr cからPまでの距離
         * @param dr dからPまでの距離
         * @returns abcdからPへの距離とabcdからxyへの距離の誤差
         */
        const abcdP = (
            x: number,
            y: number,
            ar: number,
            br: number,
            cr: number,
            dr: number
        ) => {
            const a = Math.abs(ar * mag - Math.sqrt(x ** 2 + y ** 2));
            const b = Math.abs(br * mag - Math.sqrt((x - w) ** 2 + y ** 2));
            const c = Math.abs(cr * mag - Math.sqrt(x ** 2 + (y - h) ** 2));
            const d = Math.abs(
                dr * mag - Math.sqrt((x - w) ** 2 + (y - h) ** 2)
            );
            return a + b + c + d;
        };

        const points: [number, number][] = route.map((v) => {
            let [x, y] = [a[v].latitude, a[v].longitude];
            const f = d(x, y);
            const ar = f(ulLat, ulLng);
            const br = f(urLat, urLng);
            const cr = f(llLat, llLng);
            const dr = f(lrLat, lrLng);
            let min = 10e18;
            let [pointX, pointY] = [0, 0];
            const f2 = (x: number, y: number) => abcdP(x, y, ar, br, cr, dr);
            for (let i = 0; i < w; i++) {
                for (let j = 0; j < h; j++) {
                    if (min > f2(i, j)) {
                        min = f2(i, j);
                        pointX = i;
                        pointY = j;
                    }
                }
            }
            return [pointX, pointY];
        });

        // 描画をリセット
        const ctx = getContext();
        canvasRef.current.width = canvasRef.current.width;
        if (points.length) {
            let [beforeX, beforeY] = points[0];
            ctx.fillStyle = "#f00";
            ctx.lineWidth = 3;
            ctx.strokeStyle = "#f00";
            points.forEach((p, i) => {
                const [x, y] = p;
                if (i) {
                    // 線を引く
                    ctx.moveTo(beforeX, beforeY);
                    ctx.lineTo(x, y);
                }
                // 点を打つ
                ctx.fillRect(x - 5, y - 5, 10, 10);
                [beforeX, beforeY] = [x, y];
            });
            ctx.stroke();
        }
    };

    useEffect(() => {
        (async () => {
            const res = (
                await getDoc(doc(db, "map/map").withConverter(mapConverter))
            ).data();
            if (!res) throw new Error("map document is not found");
            setShopList(res);
            res.node.floor[floor.current].forEach((val) => {
                if (val.name === destination) {
                    setDestinationLocationLatitude(val.latitude);
                    setDestinationLocationLongitude(val.longitude);
                }
            });
            if (!imgRef.current) throw new Error("image ref is null");
            imgRef.current.src =
                floor.current === 4
                    ? map_4
                    : floor.current === 5
                    ? map_5
                    : map_6;
            imgRef.current.onload = () => {
                if (!canvasRef.current) throw new Error("canvas ref is null");
                if (!imgRef.current) throw new Error("image ref is null");
                canvasRef.current.width = imgRef.current.width;
                canvasRef.current.height = imgRef.current.height;
            };
        })();
    }, []);

    useEffect(() => {
        if (shopList) dijkstra();
    }, [shopList]);

    const floorChange = (newFloor: Floor, isHand = false) => {
        if (newFloor === floor.current) return;
        floor.current = newFloor;
        isFloorChange.current = isHand || isFloorChange.current;
        if (!imgRef.current) throw new Error("image ref is null");
        imgRef.current.src =
            newFloor === 4 ? map_4 : newFloor === 5 ? map_5 : map_6;
        // if (shopList) dijkstra();
        dijkstra();
    };

    return { canvasRef, imgRef, floorChange };
};

export default useFloorMap;
